import {
  CustomChartType,
  type ICustomReportChart,
} from '@principle-theorem/principle-core/interfaces';
import {
  ReportBuilderCharts,
  getMeasureQueries,
} from '@principle-theorem/reporting';
import {
  IChartConfig,
  IReportBuilderDataSource,
} from '@principle-theorem/reporting/interfaces';
import { type Crossfilter } from 'crossfilter2';
import {
  DCDataTransformer,
  type IDCTransformResult,
  type LabelTransformFn,
} from '../../../../../models/report/charts/data-transformers/dc-data-transformer';
import { ChartBuilder } from '../../../../core/chart-builders/chart-builder';
import {
  ChartBuilderType,
  IDCChartBuilder,
  ITableBuilder,
} from '../../../../core/chart-builders/dc-chart-builders/dc-chart-builder';
import {
  ITableDisplay,
  buildDCChartDisplay,
  buildTableDisplay,
  type IDCChartDisplay,
} from '../../../../core/dynamic-dc-chart/dc-chart-display';

export interface IChartDisplay {
  chart: ICustomReportChart;
  chartDisplay?: IDCChartDisplay<unknown>;
  tableDisplay?: ITableDisplay;
}

export function getChartDisplay(
  dataSource: IReportBuilderDataSource,
  chart: ICustomReportChart,
  ndx?: Crossfilter<unknown>,
  labelTransformFn?: LabelTransformFn
): IChartDisplay | undefined {
  const builder = ChartBuilder.getForChart(chart);
  const chartFilter = ReportBuilderCharts.getFilterDefinition(
    dataSource,
    chart
  );

  if (!builder || !chartFilter) {
    // eslint-disable-next-line no-console
    console.warn(`No filter found for chart ${chart.uid}`);
    return;
  }
  if (
    builder.type === ChartBuilderType.Table &&
    chart.type === CustomChartType.Table
  ) {
    const tableDisplay = getTableDisplay(
      builder,
      chartFilter.config,
      ndx,
      labelTransformFn
    );
    if (!tableDisplay) {
      // eslint-disable-next-line no-console
      console.warn(`No table display found for chart ${chart.uid}`);
      return;
    }
    return {
      chart,
      tableDisplay,
    };
  }

  if (builder.type === ChartBuilderType.DC) {
    const chartDisplay = getDcChartDisplay(
      builder,
      chartFilter.config,
      ndx,
      labelTransformFn
    );
    if (!chartDisplay) {
      // eslint-disable-next-line no-console
      console.warn(`No chart display found for chart ${chart.uid}`);
      return;
    }
    return {
      chart,
      chartDisplay,
    };
  }
}

export function getDcChartDisplay(
  builder: IDCChartBuilder<unknown>,
  config: IChartConfig,
  ndx?: Crossfilter<unknown>,
  labelTransformFn?: LabelTransformFn
): IDCChartDisplay<unknown> | undefined {
  if (!ndx) {
    return;
  }
  const transformer = new DCDataTransformer(labelTransformFn);
  const results: IDCTransformResult[] = getMeasureQueries(config).map(
    ({ measure }) => {
      const dimension = transformer.getDimension(ndx, config, measure.measure);
      const group = transformer.getGroup(measure.measure, dimension);
      return {
        measureUid: measure.uid,
        measure: measure.measure,
        factTable: ndx,
        dimension,
        group,
        labelTransformFn,
      };
    }
  );
  return buildDCChartDisplay(builder, config, results);
}

export function getTableDisplay(
  builder: ITableBuilder,
  config: IChartConfig,
  ndx?: Crossfilter<unknown>,
  labelTransformFn?: LabelTransformFn
): ITableDisplay | undefined {
  if (!ndx) {
    return;
  }
  const transformer = new DCDataTransformer(labelTransformFn);
  const results: IDCTransformResult[] = getMeasureQueries(config).map(
    ({ measure }) => {
      const dimension = transformer.getDimension(ndx, config, measure.measure);
      const group = transformer.getGroup(measure.measure, dimension);
      return {
        measureUid: measure.uid,
        measure: measure.measure,
        factTable: ndx,
        dimension,
        group,
        labelTransformFn,
      };
    }
  );
  return buildTableDisplay(builder, config, results);
}
